import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTransactions } from '../contexts/TransactionContext';
import { useToast } from '../contexts/ToastContext';
import Icon from './Icon';
import { getCategoryIcon } from '../utils/categoryIcons';

/**
 * 카테고리 목록 — TransactionForm.js와 동일한 데이터
 * @type {Record<string, string[]>}
 */
const CATEGORIES = {
  expense: [
    '식비', '주거비', '교통비', '통신비', '공과금', '쇼핑',
    '생활용품', '뷰티/미용', '여행/레저', '문화생활', '의료/약국',
    '교육/강의', '경조사비', '반려동물', '금융/투자', '기타',
  ],
  income: ['급여', '부업', '투자수익', '용돈', '상여금', '기타'],
};

/**
 * 오늘 날짜를 YYYY-MM-DD 형식으로 반환
 * @returns {string}
 */
const getLocalDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * 폼 초기 상태
 * @returns {object}
 */
const getInitialFormData = () => ({
  type: 'expense',
  amount: '',
  category: '',
  description: '',
  date: getLocalDateString(),
});

/**
 * TransactionFAB — 플로팅 액션 버튼 + 거래 추가 모달
 *
 * 대시보드, 달력 조회, 거래 내역 페이지에서 우측 하단에 표시되는 FAB 버튼.
 * 클릭 시 모달로 거래 추가 폼을 표시한다.
 *
 * 기능:
 * - TransactionForm.js의 폼 로직을 재사용
 * - 거래 추가 성공 시 모달 닫기 + 토스트 메시지
 * - ESC 키로 모달 닫기
 * - 모달 외부(오버레이) 클릭으로 닫기
 * - 포커스 트랩 (모달 내부에서만 탭 이동)
 * - prefers-reduced-motion 대응
 *
 * @returns {React.ReactElement}
 */
const TransactionFAB = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState(getInitialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);

  const { addTransaction } = useTransactions();
  const { showToast } = useToast();

  const modalRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const fabButtonRef = useRef(null);
  const categoryDropdownRef = useRef(null);

  /**
   * 모달 열기
   */
  const openModal = useCallback(() => {
    setFormData(getInitialFormData());
    setIsOpen(true);
  }, []);

  /**
   * 모달 닫기 — 제출 중이 아닐 때만 닫을 수 있음
   */
  const closeModal = useCallback(() => {
    if (isSubmitting) return;
    setIsOpen(false);
    setIsCategoryOpen(false);
    setFormData(getInitialFormData());
    // 모달 닫힌 후 FAB 버튼으로 포커스 복귀
    setTimeout(() => {
      fabButtonRef.current?.focus();
    }, 0);
  }, [isSubmitting]);

  /**
   * ESC 키 핸들러
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeModal]);

  /**
   * 모달 열릴 때 첫 번째 입력 요소에 포커스
   */
  useEffect(() => {
    if (isOpen) {
      // 약간의 딜레이 후 포커스 (애니메이션 완료 대기)
      const timer = setTimeout(() => {
        firstFocusableRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  /**
   * 포커스 트랩 — 모달 내부에서만 탭 이동
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleTabTrap = (e) => {
      if (e.key !== 'Tab') return;

      const modal = modalRef.current;
      if (!modal) return;

      const focusableElements = modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: 첫 번째 요소에서 마지막으로 이동
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: 마지막 요소에서 첫 번째로 이동
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabTrap);
    return () => document.removeEventListener('keydown', handleTabTrap);
  }, [isOpen]);

  /**
   * 모달 열릴 때 body 스크롤 방지
   */
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  /**
   * 카테고리 드롭다운 외부 클릭 시 닫기
   */
  useEffect(() => {
    if (!isCategoryOpen) return;

    const handleClickOutside = (e) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
        setIsCategoryOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCategoryOpen]);

  /**
   * 카테고리 선택 핸들러
   */
  const handleCategorySelect = useCallback((cat) => {
    setFormData(prev => ({ ...prev, category: cat }));
    setIsCategoryOpen(false);
  }, []);

  /**
   * 폼 입력 변경 핸들러
   */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // 유형 변경 시 카테고리 초기화
      ...(name === 'type' && { category: '' }),
    }));
    if (name === 'type') {
      setIsCategoryOpen(false);
    }
  }, []);

  /**
   * 폼 제출 핸들러
   */
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!formData.amount || !formData.category) {
      showToast({ type: 'warning', message: '금액과 카테고리를 입력해주세요.' });
      return;
    }

    setIsSubmitting(true);

    try {
      await addTransaction({
        ...formData,
        amount: parseFloat(formData.amount),
      });
      showToast({ type: 'success', message: '거래가 추가되었습니다.' });
      setIsOpen(false);
      setFormData(getInitialFormData());
      // 성공 후 FAB 버튼으로 포커스 복귀
      setTimeout(() => {
        fabButtonRef.current?.focus();
      }, 0);
    } catch (error) {
      const userMessage = error?.userMessage || '거래 추가에 실패했습니다. 다시 시도해주세요.';
      showToast({ type: 'error', message: userMessage });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, addTransaction, showToast]);

  /**
   * 오버레이 클릭 핸들러 — 모달 외부 클릭 시 닫기
   */
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  }, [closeModal]);

  return (
    <>
      {/* FAB 버튼 */}
      <button
        ref={fabButtonRef}
        className="transaction-fab"
        onClick={openModal}
        aria-label="거래 추가"
        type="button"
      >
        <Icon name="add" size={24} />
      </button>

      {/* 모달 오버레이 */}
      {isOpen && (
        <div
          className="fab-modal-overlay"
          onClick={handleOverlayClick}
          role="dialog"
          aria-modal="true"
          aria-label="거래 추가"
        >
          <div
            className="fab-modal-content"
            ref={modalRef}
          >
            {/* 모달 헤더 */}
            <div className="fab-modal-header">
              <h3 className="fab-modal-title">거래 추가</h3>
              <button
                className="fab-modal-close"
                onClick={closeModal}
                aria-label="닫기"
                type="button"
                disabled={isSubmitting}
              >
                <Icon name="close" size={20} />
              </button>
            </div>

            {/* 모달 폼 */}
            <form onSubmit={handleSubmit} className="fab-modal-form">
              <div className="fab-form-group">
                <label>유형</label>
                <div className="fab-type-segment" role="radiogroup" aria-label="거래 유형">
                  <button
                    type="button"
                    ref={firstFocusableRef}
                    className={`fab-type-btn ${formData.type === 'expense' ? 'active expense' : ''}`}
                    role="radio"
                    aria-checked={formData.type === 'expense'}
                    onClick={() => {
                      setFormData(prev => ({ ...prev, type: 'expense', category: '' }));
                      setIsCategoryOpen(false);
                    }}
                  >
                    지출
                  </button>
                  <button
                    type="button"
                    className={`fab-type-btn ${formData.type === 'income' ? 'active income' : ''}`}
                    role="radio"
                    aria-checked={formData.type === 'income'}
                    onClick={() => {
                      setFormData(prev => ({ ...prev, type: 'income', category: '' }));
                      setIsCategoryOpen(false);
                    }}
                  >
                    수입
                  </button>
                </div>
              </div>

              <div className="fab-form-group">
                <label htmlFor="fab-amount">금액</label>
                <input
                  id="fab-amount"
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="금액을 입력하세요"
                  required
                  min="0"
                  step="1"
                />
              </div>

              <div className="fab-form-group">
                <label>카테고리</label>
                <div className="fab-category-dropdown" ref={categoryDropdownRef}>
                  <button
                    type="button"
                    className={`fab-category-trigger ${formData.category ? 'has-value' : ''}`}
                    onClick={() => setIsCategoryOpen(prev => !prev)}
                    aria-haspopup="listbox"
                    aria-expanded={isCategoryOpen}
                    aria-label="카테고리 선택"
                  >
                    {formData.category ? (
                      <span className="fab-category-selected">
                        <Icon name={getCategoryIcon(formData.category)} size={18} />
                        <span>{formData.category}</span>
                      </span>
                    ) : (
                      <span className="fab-category-placeholder">카테고리 선택</span>
                    )}
                    <Icon name={isCategoryOpen ? 'expand_less' : 'expand_more'} size={20} />
                  </button>

                  {isCategoryOpen && (
                    <ul className="fab-category-list" role="listbox" aria-label="카테고리 목록">
                      {CATEGORIES[formData.type].map(cat => (
                        <li
                          key={cat}
                          role="option"
                          aria-selected={formData.category === cat}
                          className={`fab-category-option ${formData.category === cat ? 'selected' : ''}`}
                          onClick={() => handleCategorySelect(cat)}
                        >
                          <Icon name={getCategoryIcon(cat)} size={18} />
                          <span>{cat}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="fab-form-group">
                <label htmlFor="fab-description">설명</label>
                <input
                  id="fab-description"
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="설명을 입력하세요"
                />
              </div>

              <div className="fab-form-group">
                <label htmlFor="fab-date">날짜</label>
                <input
                  id="fab-date"
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* 제출 버튼 */}
              <button
                type="submit"
                className="fab-modal-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="fab-submit-loading">
                    <span className="loading-spinner" />
                    추가 중...
                  </span>
                ) : (
                  <span className="fab-submit-content">
                    <Icon name="add_circle" size={20} />
                    추가하기
                  </span>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default TransactionFAB;
